import { useAuth } from "react-oidc-context";
import { CircleUser } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface MinimalErrorLayoutProps {
  children: React.ReactNode;
}

/**
 * A minimal layout shell used when the main app cannot load (e.g., profile fetch failed).
 * Provides the Lytra logo and a logout button so users can still sign out and switch accounts.
 */
export function MinimalErrorLayout({ children }: MinimalErrorLayoutProps) {
  const auth = useAuth();

  return (
    <div className="grid w-full h-screen min-h-screen grid-cols-[auto_1fr]">
      {/* Minimal sidebar with just logo */}
      <div className="flex flex-col h-full max-h-screen min-h-0 gap-2 border-r">
        <div className="flex h-14 items-center px-4 lg:h-[60px] shrink-0">
          <div className="flex items-center gap-2 font-semibold">
            <img src="/lytra_logo_dark.svg" className="size-20 ml-1 dark:hidden" />
            <img src="/lytra_logo_white.svg" className="size-20 ml-1 hidden dark:block" />
          </div>
        </div>
      </div>

      {/* Main content area with header */}
      <div className="flex flex-col max-h-screen h-screen overflow-hidden">
        <header className="flex h-14 items-center justify-end px-4 lg:h-[60px] border-b">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <CircleUser className="h-5 w-5" />
                <span className="sr-only">Menü ein-/ausblenden</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  void auth.signoutRedirect();
                }}
              >
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
          {children}
        </main>
      </div>
    </div>
  );
}
